import type { PcmPlayer } from './pcm.service';
import type { MioVoiceAudioEvent } from '@mio/shared';

type TurnAudioEvent = Exclude<MioVoiceAudioEvent, { type: 'audio.connected' }>;
const MAX_BUFFER_BYTES = 2 * 1024 * 1024;

interface PlayingTurn {
  id: string;
  nextSequence: number;
  ended: boolean;
  queue: Uint8Array[];
  queuedBytes: number;
  waiting: boolean;
  closed: boolean;
  consumed: boolean;
  abort: AbortController;
  output: ReadableStreamDefaultController<Uint8Array>;
}

/** Live audio has no replay cursor. Only locally accepted turns may reach the speaker. */
export class ServerAudioPlayback {
  private turnId: string | null = null;
  private playing: PlayingTurn | null = null;
  private ignored = new Set<string>();
  private pending = new Map<string, TurnAudioEvent[]>();
  private pendingBytes = 0;
  private emptyTurnEnded = false;

  constructor(
    private player: PcmPlayer,
    private onError: (message: string) => void,
  ) {}

  /** Receiving audio.end is not enough: queued PCM and the browser speaker must also drain. */
  get currentTurn() {
    if (!this.turnId || this.emptyTurnEnded) return null;
    if (this.playing?.ended && this.playing.consumed) {
      const playback = this.player.snapshot();
      if (playback.playedMs >= playback.generatedMs) return null;
    }
    return this.turnId;
  }

  expectTurn(turnId: string) {
    if (this.turnId === turnId || this.ignored.has(turnId)) return;
    const early = this.pending.get(turnId) ?? [];
    if (this.turnId) this.player.stop();
    this.interrupt();
    this.ignored.delete(turnId);
    this.turnId = turnId;
    for (const event of early) this.receive(event);
  }

  receive(event: TurnAudioEvent) {
    if (this.ignored.has(event.turnId)) return;
    if (event.turnId !== this.turnId) {
      const bytes = event.type === 'audio.chunk' ? Math.ceil((event.pcm.length * 3) / 4) : 128;
      // A model may begin before the POST response arrives. Bound that short race independently
      // of the playback buffer, and never play an unknown turn merely because it has audio.
      const queued = this.playing?.queuedBytes ?? 0;
      if (
        this.pendingBytes + bytes + queued > MAX_BUFFER_BYTES ||
        (!this.pending.has(event.turnId) && this.pending.size >= 4)
      ) {
        this.fail('Mio audio arrived faster than it could be accepted. Reconnect voice.');
        return;
      }
      const events = this.pending.get(event.turnId) ?? [];
      events.push(event);
      this.pending.set(event.turnId, events);
      this.pendingBytes += bytes;
      return;
    }
    if (event.type === 'audio.error') {
      this.fail(event.message);
      return;
    }
    if (!this.playing) {
      if (event.sequence !== 0) {
        this.fail('Mio missed part of the live audio. Reconnect voice.');
        return;
      }
      if (event.type === 'audio.end') {
        this.emptyTurnEnded = true;
        this.ignored.add(event.turnId);
        return;
      }
      this.begin(event.turnId);
    }
    const playing = this.playing;
    if (!playing || playing.ended) return;
    if (event.sequence !== playing.nextSequence) {
      this.fail('Mio received interrupted audio. Reconnect voice.');
      return;
    }
    if (event.type === 'audio.end') {
      playing.ended = true;
      this.deliver(playing);
      return;
    }
    try {
      const binary = atob(event.pcm);
      const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
      if (playing.queuedBytes + bytes.byteLength + this.pendingBytes > MAX_BUFFER_BYTES) {
        this.fail('Mio audio playback fell behind. Reconnect voice.');
        return;
      }
      playing.nextSequence++;
      playing.queue.push(bytes);
      playing.queuedBytes += bytes.byteLength;
      this.deliver(playing);
    } catch {
      this.fail('Mio received invalid speech audio. Reconnect voice.');
    }
  }

  private begin(turnId: string) {
    const abort = new AbortController();
    const body = new ReadableStream<Uint8Array>(
      {
        start: (output) => {
          this.playing = {
            id: turnId,
            nextSequence: 0,
            ended: false,
            abort,
            output,
            queue: [],
            queuedBytes: 0,
            waiting: false,
            closed: false,
            consumed: false,
          };
        },
        pull: () => {
          if (this.playing?.id !== turnId) return;
          this.playing.waiting = true;
          this.deliver(this.playing);
        },
      },
      { highWaterMark: 0 },
    );
    const response = new Response(body, {
      headers: { 'Content-Type': 'audio/pcm', 'X-Audio-Sample-Rate': '24000' },
    });
    void this.player
      .stream(response, abort.signal)
      .then(() => {
        if (!abort.signal.aborted && this.playing?.id === turnId) this.playing.consumed = true;
      })
      .catch((cause: unknown) => {
        if (!abort.signal.aborted && this.turnId === turnId)
          this.fail(cause instanceof Error ? cause.message : 'Speech playback disconnected.');
      });
  }

  private deliver(playing: PlayingTurn) {
    if (playing.closed) return;
    if (playing.waiting && playing.queue.length) {
      const bytes = playing.queue.shift();
      if (bytes) {
        playing.queuedBytes -= bytes.byteLength;
        playing.waiting = false;
        playing.output.enqueue(bytes);
      }
    }
    if (playing.ended && !playing.queue.length) {
      playing.closed = true;
      playing.output.close();
    }
  }

  private fail(message: string) {
    this.interrupt();
    this.onError(message);
  }

  interrupt() {
    if (this.turnId) this.ignored.add(this.turnId);
    for (const id of this.pending.keys()) this.ignored.add(id);
    this.pending.clear();
    this.pendingBytes = 0;
    const playing = this.playing;
    this.playing = null;
    this.turnId = null;
    this.emptyTurnEnded = false;
    if (playing) {
      playing.abort.abort();
      playing.queue = [];
      playing.queuedBytes = 0;
      if (!playing.closed) {
        playing.closed = true;
        playing.ended = true;
        try {
          playing.output.close();
        } catch {
          // The player's cancellation may already have closed its reader.
        }
      }
    }
  }
}
